import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, paperClasses, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Billing_Type_Enum } from '@kheops/graphql';
import { InstantSearch } from 'react-instantsearch';
import { orderAtom, viewOrderItemsAtom } from '../../state/state';
import PackagingDisplayBox from '../../../common/components/PackagingDisplayBox';
import DialogCloseButton from '../../../common/components/DialogCloseButton';
import { useSearchClient } from '../../../hooks/useSearchClient';
import AddPackagingsButton from '../AddPackagingsButton';

export interface OrderBuyerReceptionDialogProps {
  open: boolean;
  onClose: (value: boolean) => void;
}

export default function OrderBuyerReceptionDialog({ open, onClose }: OrderBuyerReceptionDialogProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'order']);
  const items = useAtomValue(viewOrderItemsAtom);
  const order = useAtomValue(orderAtom);
  const searchClient = useSearchClient();

  const packagings = useMemo(() => {
    return items.map((item, index) => {
      const canEditWeight = item.packaging.base_unit?.billing_type === Billing_Type_Enum.WeightDependent;

      return (
        <PackagingDisplayBox
          key={item.packaging.id}
          packagingDisplay={item.packaging}
          orderStatus={order.status}
          canEditQuantity
          canEditWeight={canEditWeight}
          shouldDisplayAddedPackagingBadge={item.hasJustBeenAdded || item.notPresentInPreviousVersion}
          index={index}
        />
      );
    });
  }, [items]);

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      sx={{
        [`& .${paperClasses.root}`]: {
          minWidth: {
            sm: 600,
            md: 724,
            lg: 960,
          },
        },
      }}
    >
      <DialogCloseButton onClick={() => onClose(false)} />
      <DialogTitle>
        <Typography>
          {t('order:order_reception')}
        </Typography>
        <Typography>
          {t('order:verification_quantities_and_weights_detailed')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            rowGap: 1,
            mb: 1.5,
          }}
        >
          <Typography>
            {t('order:check_products')}
          </Typography>
          <InstantSearch
            indexName={import.meta.env.REACT_APP_ALGOLIA_PACKAGING_INDEX}
            searchClient={searchClient}
          >
            <AddPackagingsButton
              supplierCompanyId={order.contract.supplyingCompanyId}
              buyerCompanyId={order.contract.buyingCompanyId}
              excludedObjectIds={items.map((pq) => `${pq.packaging.sku}-${pq.packaging.company_id}`)}
            />
          </InstantSearch>
        </Box>
        {packagings}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>
          {t('common:cancel')}
        </Button>
        <Button onClick={() => onClose(true)} variant="contained">
          {t('order:receive_order')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
